import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { RotateCcw } from "lucide-react";
import { impossibleDeadline } from "@/scenarios/impossible-deadline";
import { startProject, advanceProject, advanceUntilActionable, isGameOver, resolveOutcome } from "@/engine/simulate";
import { METRIC_KEYS, INVERTED_METRICS, type ProjectState, type Effect } from "@/engine/types";
import MetricBar from "@/components/simulation/MetricBar";
import DecisionLog, { EffectChips } from "@/components/simulation/DecisionLog";
import RetrospectivePanel from "@/components/simulation/RetrospectivePanel";

/** Single-session screen switch — Landing / Playing / Retrospective are states, not routes. */
type Screen = "landing" | "playing" | "retrospective";

const SENDER_AVATAR: Record<string, string> = {
  Director: "🧑‍💼",
  Security: "🛡️",
  "BI Developer": "📊",
  "Data Engineer": "🖥️",
  "Working Session": "👥",
  "Business Owner": "💼",
};

function overallScore(state: ProjectState): number {
  const healthy = METRIC_KEYS.map((k) => (INVERTED_METRICS.includes(k) ? 100 - state[k] : state[k]));
  return Math.round(healthy.reduce((a, b) => a + b, 0) / healthy.length);
}

export default function PMSimGame() {
  const scenario = impossibleDeadline;
  const [screen, setScreen] = useState<Screen>("landing");
  const [state, setState] = useState<ProjectState>(() => startProject(scenario));
  const [lastResult, setLastResult] = useState<string | null>(null);
  const [lastEffects, setLastEffects] = useState<Effect[] | null>(null);

  const activeEvent = useMemo(
    () => scenario.events.find((e) => e.id === state.activeEventIds[0]),
    [scenario, state.activeEventIds]
  );

  const start = () => {
    setState(startProject(scenario));
    setLastResult(null);
    setLastEffects(null);
    setScreen("playing");
  };

  const restart = () => {
    setState(startProject(scenario));
    setLastResult(null);
    setLastEffects(null);
    setScreen("landing");
  };

  const choose = (choiceId: string) => {
    if (!activeEvent) return;
    const choice = activeEvent.choices.find((c) => c.id === choiceId);
    let next = advanceProject(scenario, state, activeEvent.id, choiceId);
    next = advanceUntilActionable(scenario, next);
    setLastResult(choice?.resultText ?? null);
    setLastEffects(choice?.effects ?? null);
    setState(next);
    if (isGameOver(scenario, next)) setScreen("retrospective");
  };

  const overall = overallScore(state);

  if (screen === "landing") {
    return (
      <div className="p-6 lg:p-8 max-w-3xl">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl leading-none">🏥</span>
          <h1 className="text-2xl font-display font-bold text-foreground">
            Enterprise Project Manager
          </h1>
        </div>
        <p className="text-sm text-muted-foreground font-body mb-6">
          Level 1 — Estimation. You're the newly hired Data Systems Analyst moving an
          enterprise analytics pipeline to AWS at a large academic health system.
        </p>
        <Card className="p-6 bg-card border-border mb-6">
          <h2 className="text-base font-display font-bold text-foreground mb-3">
            {scenario.title}
          </h2>
          <div className="space-y-2">
            {scenario.premise.map((line, i) => (
              <p key={i} className="text-sm font-body text-card-foreground leading-relaxed">
                {line}
              </p>
            ))}
          </div>
        </Card>
        <Button onClick={start}>Start Simulation</Button>
      </div>
    );
  }

  if (screen === "retrospective") {
    const outcome = resolveOutcome(scenario, state);
    return (
      <div className="p-6 lg:p-8 max-w-5xl">
        <RetrospectivePanel state={state} outcome={outcome} onRestart={restart} />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-5xl">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-2xl leading-none">🏥</span>
            <h1 className="text-2xl font-display font-bold text-foreground">
              Enterprise Project Manager
            </h1>
          </div>
          <p className="text-sm text-muted-foreground font-body mt-1">{scenario.title}</p>
        </div>
        <Button variant="outline" size="sm" onClick={restart} className="shrink-0">
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          Restart
        </Button>
      </div>

      <div className="grid lg:grid-cols-[1fr_260px] gap-6">
        <div>
          {activeEvent && (
            <Card className="p-6 bg-card border-border">
              <div className="flex items-center gap-2 mb-4">
                <Badge variant="secondary" className="font-mono text-[10px]">
                  {activeEvent.day}
                </Badge>
              </div>

              <div className="flex items-start gap-3 mb-6">
                <span className="shrink-0 h-11 w-11 rounded-full bg-muted flex items-center justify-center text-2xl leading-none border border-border">
                  {SENDER_AVATAR[activeEvent.sender] ?? "👤"}
                </span>
                <div className="flex-1">
                  <p className="text-xs font-semibold font-body text-foreground mb-1">
                    {activeEvent.sender}
                  </p>
                  <div className="rounded-2xl rounded-tl-sm bg-muted/60 border border-border px-4 py-3 space-y-2">
                    <p className="text-sm font-display font-bold text-foreground">{activeEvent.title}</p>
                    {activeEvent.body.map((line, i) => (
                      <p key={i} className="text-sm font-body text-card-foreground leading-relaxed">
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
              </div>

              {lastResult && (
                <div className="mb-2 ml-14 rounded-xl border border-accent/30 bg-accent/5 p-3">
                  <p className="text-[11px] font-semibold text-accent mb-1 font-body">
                    Previous outcome
                  </p>
                  <p className="text-xs text-muted-foreground font-body leading-relaxed">
                    {lastResult}
                  </p>
                </div>
              )}
              {lastEffects && (
                <div className="mb-6 ml-14">
                  <EffectChips effects={lastEffects} />
                </div>
              )}

              <p className="text-xs font-semibold text-muted-foreground font-body mb-3">
                What do you do?
              </p>
              <div className="space-y-2">
                {activeEvent.choices.map((choice) => (
                  <button
                    key={choice.id}
                    onClick={() => choose(choice.id)}
                    className="w-full text-left border border-border rounded-xl p-3.5 hover:border-accent hover:bg-accent/5 hover:scale-[1.01] transition-all group"
                  >
                    <p className="text-sm font-body font-semibold text-card-foreground">
                      {choice.text}
                    </p>
                    {choice.description && (
                      <p className="text-xs font-body text-muted-foreground mt-0.5">
                        {choice.description}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card className="p-4 bg-card border-border">
            <div className="flex justify-between items-baseline mb-3">
              <p className="text-xs font-semibold font-body text-foreground">Scorecard</p>
              <span className="text-[11px] font-mono text-muted-foreground">Day {state.day}</span>
            </div>
            <div className="space-y-3">
              {METRIC_KEYS.map((k) => (
                <MetricBar key={k} metric={k} value={state[k]} />
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-border">
              <div className="flex justify-between text-xs font-body font-semibold text-foreground mb-1">
                <span>Overall</span>
                <span className="font-mono">{overall}</span>
              </div>
              <Progress value={overall} className="h-2" />
            </div>
          </Card>

          {state.history.length > 0 && (
            <Card className="p-4 bg-card border-border">
              <p className="text-xs font-semibold font-body text-foreground mb-3">
                Decision Log
              </p>
              <DecisionLog history={state.history} />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
